/** Value → display string, by attribute_def data type. Unassessed never becomes "0" or "—". */
import { formatMoney } from './money';
import type { AttrCell, AttributeDto, OptionDto } from '@/api/types';
import { isUnassessed } from './maturity';

export const UNASSESSED_LABEL = 'Unassessed';

export function optionFor(attr: AttributeDto, cell: AttrCell | undefined): OptionDto | null {
  if (!cell || !attr.options?.length) return null;
  if (cell.optionId) {
    const byId = attr.options.find((o) => o.id === cell.optionId);
    if (byId) return byId;
  }
  if (cell.value === null || cell.value === undefined) return null;
  const v = String(cell.value);
  return attr.options.find((o) => o.code === v || o.label === v || String(o.numericValue) === v) ?? null;
}

export function optionsFor(attr: AttributeDto, cell: AttrCell | undefined): OptionDto[] {
  if (!cell || !attr.options?.length) return [];
  const codes = cell.optionCodes ?? (Array.isArray(cell.value) ? (cell.value as unknown as string[]) : []);
  return codes.map((c) => attr.options!.find((o) => o.code === c || o.id === c)).filter((o): o is OptionDto => Boolean(o));
}

/** Money: THE formatter lives in lib/money (minor-unit arithmetic, #78/#147) and is exported here unchanged — $1.2M, $45,000. */
export { formatMoney };

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

/**
 * R16 T-137 (design v1.2 change 04): THE date and time formatter. Every date on screen reads
 * 2026-10-25, every time 2:05 PM (audit entries 2:05:09 PM), month axes Oct 2026. The locale is
 * fixed here, never the browser's; the date is assembled from Intl parts, never
 * toISOString().slice (UTC — the wrong day in the evening west of Greenwich). A bare yyyy-MM-dd
 * (a DATE column) is a calendar day and is shown as written: it never slides by a time zone.
 * The lint (web/scripts/lint-tokens.ts, format-outside-lib) bans toLocale*String elsewhere.
 */
export const LOCALE = 'en-US';
/** The one date pattern (John, 2026-10-25). Date inputs show and accept it too. */
export const DATE_PATTERN = 'yyyy-MM-dd';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Relative time switches to the date after this many days. */
export const RELATIVE_DAYS = 7;

export type DateInput = string | number | Date | null | undefined;
export interface DateFormatOptions {
  /** An IANA zone; default the user's own. A zone that is not the user's is named after the time. */
  timeZone?: string;
}

const cache = new Map<string, Intl.DateTimeFormat>();
function intl(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCALE, options);
    cache.set(key, f);
  }
  return f;
}
const spaces = (s: string) => s.replace(/[\u202f\u00a0]/g, ' ');

function asDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fallback(value: DateInput): string {
  return typeof value === 'string' ? value : '';
}

function pattern(year: string, month: string, day: string): string {
  return DATE_PATTERN.replace('yyyy', year).replace('MM', month).replace('dd', day);
}

/** A calendar day, yyyy-MM-dd. '' for nothing; unparseable text comes back as it was. */
export function formatDate(value: DateInput, options: DateFormatOptions = {}): string {
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value.trim());
    if (m) return pattern(m[1]!, m[2]!, m[3]!);
  }
  const d = asDate(value);
  if (!d) return fallback(value);
  const tz = options.timeZone;
  const parts = intl(`d|${tz ?? ''}`, { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return pattern(get('year'), get('month'), get('day'));
}

/** 2:05 PM, or 2:05:09 PM with seconds. */
export function formatTime(value: DateInput, options: DateFormatOptions & { seconds?: boolean } = {}): string {
  const d = asDate(value);
  if (!d) return fallback(value);
  const tz = options.timeZone;
  const f = intl(`t|${tz ?? ''}|${options.seconds ? 's' : ''}`, {
    hour: 'numeric',
    minute: '2-digit',
    ...(options.seconds ? { second: '2-digit' } : {}),
    hour12: true,
    timeZone: tz,
  });
  let text = spaces(f.format(d));
  if (tz && tz !== userTimeZone()) {
    const zone = intl(`z|${tz}`, { timeZone: tz, timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName')?.value;
    if (zone) text = `${text} ${zone}`;
  }
  return text;
}

/** 2026-10-25 2:05 PM; with `seconds` (audit entries only) 2026-10-25 2:05:09 PM. */
export function formatDateTime(value: DateInput, options: DateFormatOptions & { seconds?: boolean } = {}): string {
  const d = asDate(value);
  if (!d) return fallback(value);
  return `${formatDate(d, options)} ${formatTime(d, options)}`;
}

/** Oct 2026 — roadmap axes, where a bare 2026-10 reads poorly. A yyyy-MM-dd day keeps its month. */
export function formatMonth(value: DateInput, options: DateFormatOptions = {}): string {
  let d = asDate(value);
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value.trim());
    if (m) d = new Date(Number(m[1]), Number(m[2]) - 1, 15);
  }
  if (!d) return fallback(value);
  return spaces(intl(`m|${options.timeZone ?? ''}`, { month: 'short', year: 'numeric', timeZone: options.timeZone }).format(d));
}

/**
 * "just now", "5 minutes ago", "3 days ago" — and the date (2026-10-25) after seven days. Pair it
 * with formatDateTime in the tooltip (`title`), so the full date and time is one hover away.
 */
export function formatRelative(value: DateInput, now: number = Date.now()): string {
  const d = asDate(value);
  if (!d) return '';
  const mins = Math.trunc((now - d.getTime()) / 60_000);
  if (Math.abs(mins) < 1) return 'just now';
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
  // #87 4382576426: a day is a calendar day in the user's zone — 23:00 yesterday is "yesterday" at 00:30 today
  const days = calendarDaysBetween(d, new Date(now));
  if (days === 0 && Math.abs(mins) < 60) return rtf.format(-mins, 'minute');
  if (days === 0) return rtf.format(-Math.round(mins / 60), 'hour');
  if (Math.abs(days) <= RELATIVE_DAYS) return rtf.format(-days, 'day');
  return formatDate(d);
}

/** Whole calendar days from `from` to `to` in the user's zone (DST-safe: counted on local dates, not elapsed hours). Pure. */
export function calendarDaysBetween(from: Date, to: Date): number {
  const day = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()) / 86_400_000;
  return day(to) - day(from);
}
/** @deprecated the old name — formatRelative. */
export const relativeTime = formatRelative;

export function userTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** A size in words: 812 bytes, 41 KB, 1 MB, 12.4 MB (1 KB = 1024 bytes; a unit rolls over at 1024). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes / 1024;
  let i = 0;
  while (i < units.length - 1 && Math.round(n) >= 1024) {
    n /= 1024;
    i++;
  }
  const text = i === 0 ? String(Math.round(n)) : n.toFixed(1).replace(/\.0$/, '');
  return `${new Intl.NumberFormat(LOCALE).format(Number(text))} ${units[i]}`;
}

/** Display text for a cell. Returns '' for Unassessed — callers render the Unassessed state. */
export function formatValue(attr: AttributeDto, cell: AttrCell | undefined): string {
  if (isUnassessed(cell)) return '';
  const value = cell!.value;
  if (cell!.display) return cell!.display;

  switch (attr.dataType) {
    case 'single_select': {
      const opt = optionFor(attr, cell);
      return opt?.label ?? String(value ?? '');
    }
    case 'multi_select': {
      const opts = optionsFor(attr, cell);
      return opts.length ? opts.map((o) => o.label).join(', ') : String(value ?? '');
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'money':
      return typeof value === 'number' ? formatMoney(value, attr.constraints?.currency) : String(value ?? '');
    case 'percent':
      return typeof value === 'number' ? `${formatNumber(value)}%` : String(value ?? '');
    case 'integer':
    case 'decimal':
      return typeof value === 'number' ? formatNumber(value) : String(value ?? '');
    case 'date':
      return typeof value === 'string' ? formatDate(value) : String(value ?? '');
    case 'timestamp':
      return typeof value === 'string' ? formatDateTime(value) : String(value ?? '');
    default:
      return String(value ?? '');
  }
}

/** "v12-cib-intake-view" → "V12 CIB intake view" for provenance badges. */
export function sourceLabel(source: string): string {
  const known: Record<string, string> = {
    'v12-cib-intake-view': 'V12 CIB Intake View',
    'v12-function-tab': 'V12 function tab',
    v6: 'V6 workbook',
    'roadmap-yaml': 'roadmap source file',
    system: 'the system',
    ui: 'Edited in the app',
    mcp: 'Set by an agent',
    api: 'Set via API',
    seed: 'Starting data',
    llm: 'AI extraction',
  };
  return known[source] ?? source;
}

/**
 * #328/#310: the number a cell compares and sorts by — a single-select's `numericValue` (its
 * `value` is now the option code), else a numeric `value`. null when there is none.
 */
export function cellNumber(cell: Pick<AttrCell, 'value' | 'numericValue'> | undefined | null): number | null {
  if (!cell) return null;
  if (typeof cell.numericValue === 'number' && Number.isFinite(cell.numericValue)) return cell.numericValue;
  if (cell.value === null || cell.value === undefined || cell.value === '') return null;
  const n = typeof cell.value === 'number' ? cell.value : Number(cell.value);
  return Number.isFinite(n) ? n : null;
}
